// api_distributed.ts - Updated API calls for distributed AI testing system

import { TestResultDB } from './types';
import { resolveFrontendApiBase } from '../../utils/apiBase';
import adminAxios from '../adminAxios';

const API_BASE = resolveFrontendApiBase()

// Distributed batch testing types
export interface DistributedBatchRequest {
  instruction_id: string;
  instruction_text: string;
  text_ids: string[];
  texts: Array<{
    id: string;
    name: string;
    text: string;
    category?: string;
  }>;
  target_language: string;
  translation_models: string[];
  evaluation_models?: string[];
  num_runs: number;
  test_name: string;
  max_concurrent_jobs?: number;
  job_timeout_minutes?: number;
  french_em_dash_enforce?: boolean;
  french_em_dash_instructions?: string;
  proofreader_enabled?: boolean;
  proofreader_formality_model?: string;
  proofreader_general_model?: string;
  proofreader_alignment_model?: string;
  // Testing-only flag to enable backend testing behavior (e.g., em-dash enforcement)
  testing?: boolean;
}

export interface DistributedBatchResponse {
  success: boolean;
  batch_id: string;
  status: string;
  total_jobs: number;
  jobs_queued: number;
  estimated_duration_minutes: number;
  progress_url: string;
}

export interface BatchProgress {
  batch_id: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress_percentage: number;
  completed_jobs: number;
  failed_jobs: number;
  running_jobs: number;
  pending_jobs: number;
  total_jobs: number;
  estimated_remaining_minutes?: number;
  last_updated: string;
  results_available: number;
}

export interface ActiveBatch {
  batch_id: string;
  status: string;
  progress_percentage: number;
  total_jobs: number;
  completed_jobs: number;
  failed_jobs: number;
  created_at: string;
  config: {
    test_name: string;
    target_language: string;
    num_texts: number;
    num_runs: number;
    translation_models: string[];
    evaluation_models: string[];
    proofreader_enabled?: boolean;
    proofreader_formality_model?: string;
    proofreader_general_model?: string;
    proofreader_alignment_model?: string;
  };
}


// Main distributed batch processing function
export const runDistributedBatchTest = async (request: DistributedBatchRequest): Promise<DistributedBatchResponse> => {
  const { data } = await adminAxios.post<DistributedBatchResponse>(
    `${API_BASE}/api/ai-testing-batch-distributed`,
    request,
  );
  return data;
};

// Progress tracking
export const getBatchProgress = async (batchId: string): Promise<BatchProgress> => {
  const response = await fetch(`${API_BASE}/api/ai-testing-batch-progress/${batchId}`, {
    method: 'GET',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get batch progress: ${errorText}`);
  }

  return await response.json();
};

// Cancel batch job
export const cancelBatchJob = async (batchId: string): Promise<{ success: boolean; message: string; note?: string }> => {
  const response = await fetch(`${API_BASE}/api/ai-testing-batch-cancel/${batchId}`, {
    method: 'POST',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to cancel batch job: ${errorText}`);
  }

  return await response.json();
};

// Get batch results
export const getBatchResults = async (batchId: string, limit: number = 100): Promise<TestResultDB[]> => {
  const response = await fetch(`${API_BASE}/api/ai-testing-batch-results/${batchId}?limit=${limit}`, {
    method: 'GET',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get batch results: ${errorText}`);
  }

  return await response.json();
};


// New: List batches across any status (queued, running, completed, failed, cancelled, or all)
export const listBatches = async (
  limit: number = 100,
  status?: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled' | 'all'
): Promise<ActiveBatch[]> => {
  const params = new URLSearchParams();
  if (limit) params.set('limit', String(limit));
  if (status) params.set('status', status);
  const url = `${API_BASE}/api/ai-testing-batches${params.toString() ? `?${params.toString()}` : ''}`;

  const response = await fetch(url, { method: 'GET' });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to list batches: ${errorText}`);
  }
  return await response.json();
};


// Utility functions for progress monitoring
export class BatchProgressMonitor {
  private batchId: string;
  private intervalId: NodeJS.Timeout | null = null;
  private callbacks: {
    onProgress?: (progress: BatchProgress) => void;
    onComplete?: (progress: BatchProgress) => void;
    onError?: (error: Error) => void;
  } = {};

  constructor(batchId: string) {
    this.batchId = batchId;
  }

  public onProgress(callback: (progress: BatchProgress) => void) {
    this.callbacks.onProgress = callback;
    return this;
  }

  public onComplete(callback: (progress: BatchProgress) => void) {
    this.callbacks.onComplete = callback;
    return this;
  }

  public onError(callback: (error: Error) => void) {
    this.callbacks.onError = callback;
    return this;
  }

  public start(intervalMs: number = 5000) {
    if (this.intervalId) {
      this.stop();
    }

    this.intervalId = setInterval(async () => {
      try {
        const progress = await getBatchProgress(this.batchId);
        
        this.callbacks.onProgress?.(progress);

        // Check if batch is complete
        if (progress.status === 'completed' || progress.status === 'failed' || progress.status === 'cancelled') {
          this.callbacks.onComplete?.(progress);
          this.stop();
        }
      } catch (error) {
        this.callbacks.onError?.(error as Error);
        this.stop();
      }
    }, intervalMs);

    // Do initial check immediately
    this.checkProgress();
  }

  public stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private async checkProgress() {
    try {
      const progress = await getBatchProgress(this.batchId);
      this.callbacks.onProgress?.(progress);
    } catch (error) {
      this.callbacks.onError?.(error as Error);
    }
  }
}


/**
 * Delete a single batch and optionally cascade-delete related artifacts.
 * @param batchId The batch_id to delete
 * @param opts.deleteResults If true, also delete testResults with this batch_id
 * @param opts.deleteJobs If true, also delete job_status docs with this batch_id (for jobs created after tagging)
 */
export const deleteBatch = async (
  batchId: string,
  opts?: { deleteResults?: boolean; deleteJobs?: boolean }
): Promise<{ success: boolean; batch_id: string; deleted_results: number; deleted_job_status: number }> => {
  const params = new URLSearchParams({
    delete_results: String(!!opts?.deleteResults),
    delete_jobs: String(!!opts?.deleteJobs),
  });
  const res = await fetch(`${API_BASE}/api/ai-testing-batches/${batchId}?${params.toString()}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || 'Failed to delete batch');
  }
  return res.json();
};

/**
 * Prune old batches older than N days. Optionally cascade-delete related artifacts.
 * @param olderThanDays Threshold in days
 * @param opts.deleteResults If true, also delete testResults for pruned batches
 * @param opts.deleteJobs If true, also delete job_status docs for pruned batches
 */
export const pruneOldBatches = async (
  olderThanDays: number,
  opts?: { deleteResults?: boolean; deleteJobs?: boolean }
): Promise<{ success: boolean; older_than_days: number; deleted_batches: number; deleted_results_total: number; deleted_job_status_total: number }> => {
  const params = new URLSearchParams({
    older_than_days: String(olderThanDays),
    delete_results: String(!!opts?.deleteResults),
    delete_jobs: String(!!opts?.deleteJobs),
  });
  const res = await fetch(`${API_BASE}/api/ai-testing-batches?${params.toString()}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || 'Failed to prune batches');
  }
  return res.json();
};
